// API layer for Invitation - External (DMUT).
//
// TODO(API): there is no backend contract for this screen yet, so every function
// here resolves from the in-memory store instead of hitting the network. The
// interface is deliberately the same shape the live layer will have — async,
// pagination in/`{ items, totalCount }` out, rejects with a string message the way
// crude_api_helper does — so wiring the real endpoints means editing only this
// file, not the views.
//
// When the contract arrives, expect roughly:
//   list        -> GET    {crudeHost}/v1/InvitationExternal          (page/pageSize/searchText/sortField/sortOrder)
//   getDetail   -> GET    {crudeHost}/v1/InvitationExternal/{id}
//   accept      -> POST   {crudeHost}/v1/InvitationExternal/{id}/Accept
//   decline     -> POST   {crudeHost}/v1/InvitationExternal/{id}/Decline   { reason }
//   downloadDoc -> GET    {crudeHost}/v1/InvitationExternal/{id}/Document/{documentId}/Download  (blob)
// Host must be confirmed with curl before wiring — the contract's stated base URL
// has been wrong before (AD-005), and Planning/Procurement live on the crude host
// while some master lookups do not.
//
// `sortField` here is the UI's camelCase column key, translated by the store. The
// live version will need the PascalCase, server-whitelisted spelling instead —
// keep that translation in this file so the views stay unchanged.

import * as store from './invitationExternalStore';
import { DEFAULT_PAGE_SIZE } from './invitationExternalConstants';

// Simulated latency, so loading states are actually exercised in dev rather than
// resolving in the same tick and never rendering.
const LATENCY_MS = 150;

const settle = (producer, delay = LATENCY_MS) =>
  new Promise((resolve, reject) => {
    setTimeout(() => {
      try {
        resolve(producer());
      } catch (error) {
        // crude_api_helper rejects with a plain string; callers do
        // `toast.error(`...: ${error}`)`. Match that so nothing changes later.
        reject(error.message || String(error));
      }
    }, delay);
  });

// List (paginated + searched + sorted server-side, as the real endpoint will be).
export const listInvitations = ({
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  searchText = '',
  sortField = null,
  sortOrder = 'asc',
} = {}) =>
  settle(() =>
    store.list({
      search: searchText,
      page,
      pageSize,
      sortField,
      sortDir: sortOrder === 'desc' ? 'desc' : 'asc',
    })
  );

// Detail — rejects 'Invitation not found' so the view can show its not-found
// panel, mirroring how TenderOpeningDetailView keys off the 404 message.
export const getInvitation = id =>
  settle(() => {
    const found = store.getById(id);
    if (!found) throw new Error('Invitation not found');
    return found;
  });

// Accept — confirmation is the caller's job; this is the commit.
export const acceptInvitation = id => settle(() => store.accept(id));

// Decline — `reason` is mandatory and validated in the store, so the rule holds
// even if a caller forgets to check.
export const declineInvitation = (id, reason) =>
  settle(() => store.decline(id, reason));

// Documents: preview and download both need the file bytes, which do not exist
// while this module is dummy-backed. Both reject with a clear message so the
// views can toast it instead of pretending to succeed.
export const downloadDocument = (invitationId, documentId) =>
  settle(() => {
    const record = store.getById(invitationId);
    const doc = (record?.supportingDocuments || []).find(
      d => d.id === documentId
    );
    if (!doc) throw new Error('Document not found');
    throw new Error(
      `No file storage wired yet for "${doc.fileName}" — pending the backend contract`
    );
  });
